import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const SVG_NS = "http://www.w3.org/2000/svg";

export default () => ({
    ticks: [],
    scrollTrigger: null,
    frame: null,
    resizeObserver: null,
    resizeTimeout: null,
    startedAt: 0,
    currentProgress: 0,
    currentOffset: 0,
    prefersReducedMotion: false,
    baseConfig: null,

    config: {
        count: 252,
        cx: 500,
        cy: 500,
        radius: 385,
        tickLength: 20,
        tickWidth: 3,
        startAngle: 244,
        activeArc: 205,
        idleSpeed: 0.018,
        inactiveColor: "rgba(0, 10, 22, 0.82)",
        inactiveOpacity: 0.9,
        colors: ["#2A5A4D", "#42C6AC", "#62E6D9"],
    },

    init() {
        this.prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        this.readConfig();
        this.baseConfig = { ...this.config };
        this.applyResponsiveConfig();
        this.createTicks();
        this.setupResizeObserver();

        if (this.$el.dataset.orbitMode === "idle") {
            this.startIdle();
            return;
        }

        this.startScroll();
    },

    destroy() {
        if (this.scrollTrigger) {
            this.scrollTrigger.kill();
            this.scrollTrigger = null;
        }

        if (this.frame) {
            cancelAnimationFrame(this.frame);
            this.frame = null;
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }

        clearTimeout(this.resizeTimeout);
    },

    readConfig() {
        const numberOptions = {
            orbitCount: "count",
            orbitRadius: "radius",
            orbitTickLength: "tickLength",
            orbitTickWidth: "tickWidth",
            orbitStartAngle: "startAngle",
            orbitActiveArc: "activeArc",
            orbitIdleSpeed: "idleSpeed",
        };

        Object.entries(numberOptions).forEach(([dataKey, configKey]) => {
            if (this.$el.dataset[dataKey] === undefined) {
                return;
            }

            const value = parseFloat(this.$el.dataset[dataKey]);

            if (!Number.isNaN(value)) {
                this.config[configKey] = value;
            }
        });

        if (this.$el.dataset.orbitInactiveColor) {
            this.config.inactiveColor = this.$el.dataset.orbitInactiveColor;
        }

        if (this.$el.dataset.orbitColors) {
            const colors = this.$el.dataset.orbitColors
                .split(",")
                .map(color => color.trim())
                .filter(Boolean);

            if (colors.length >= 2) {
                this.config.colors = colors;
            }
        }
    },

    applyResponsiveConfig() {
        if (!this.baseConfig) {
            return;
        }

        this.config = { ...this.baseConfig };

        const suffix = this.getResponsiveSuffix();
        const numberOptions = {
            orbitCount: "count",
            orbitRadius: "radius",
            orbitTickLength: "tickLength",
            orbitTickWidth: "tickWidth",
            orbitStartAngle: "startAngle",
            orbitActiveArc: "activeArc",
            orbitIdleSpeed: "idleSpeed",
        };

        Object.entries(numberOptions).forEach(([dataKey, configKey]) => {
            const responsiveKey = `${dataKey}${suffix}`;

            if (this.$el.dataset[responsiveKey] === undefined) {
                return;
            }

            const value = parseFloat(this.$el.dataset[responsiveKey]);

            if (!Number.isNaN(value)) {
                this.config[configKey] = value;
            }
        });
    },

    getResponsiveSuffix() {
        if (window.innerWidth < 768) {
            return "Mobile";
        }

        if (window.innerWidth < 1024) {
            return "Tablet";
        }

        return "Desktop";
    },

    setupResizeObserver() {
        if (!window.ResizeObserver) {
            return;
        }

        this.resizeObserver = new ResizeObserver(() => {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.handleResize();
            }, 120);
        });

        this.resizeObserver.observe(this.$el);
    },

    handleResize() {
        const previousGeometry = this.getGeometrySignature();
        const progress = this.currentProgress;
        const offset = this.currentOffset;

        this.applyResponsiveConfig();

        if (previousGeometry !== this.getGeometrySignature()) {
            this.createTicks();
        }

        if (this.scrollTrigger) {
            this.scrollTrigger.refresh();
        }

        this.render(progress, offset);
    },

    getGeometrySignature() {
        return [
            this.config.count,
            this.config.radius,
            this.config.tickLength,
            this.config.tickWidth,
        ].join(":");
    },

    createTicks() {
        const ticksLayer = this.$el.querySelector(".orbit-ticks__ticks");

        if (!ticksLayer) {
            return;
        }

        ticksLayer.innerHTML = "";
        this.ticks = [];

        for (let i = 0; i < this.config.count; i++) {
            const angle = (i / this.config.count) * 360;
            const coordinates = this.getTickCoordinates(angle);
            const line = this.createLine(coordinates, this.config.tickWidth);

            ticksLayer.appendChild(line);
            this.ticks.push({ angle, line });
        }

        this.render(0);
    },

    createLine({ x1, y1, x2, y2 }, width) {
        const line = document.createElementNS(SVG_NS, "line");

        line.setAttribute("x1", x1);
        line.setAttribute("y1", y1);
        line.setAttribute("x2", x2);
        line.setAttribute("y2", y2);
        line.setAttribute("stroke-width", width);
        line.setAttribute("stroke-linecap", "butt");

        return line;
    },

    getTickCoordinates(angle) {
        const radians = ((angle - 90) * Math.PI) / 180;
        const innerRadius = this.config.radius;
        const outerRadius = this.config.radius + this.config.tickLength;
        const x = Math.cos(radians);
        const y = Math.sin(radians);

        return {
            x1: this.config.cx + x * innerRadius,
            y1: this.config.cy + y * innerRadius,
            x2: this.config.cx + x * outerRadius,
            y2: this.config.cy + y * outerRadius,
        };
    },

    startScroll() {
        const trigger = this.getTriggerElement();

        if (!trigger) {
            this.render(1);
            return;
        }

        this.scrollTrigger = ScrollTrigger.create({
            trigger,
            start: "top top",
            end: "bottom bottom",
            scrub: true,
            onUpdate: self => this.render(self.progress),
        });

        this.render(this.scrollTrigger.progress || 0);
    },

    startIdle() {
        this.render(1);

        if (this.prefersReducedMotion) {
            return;
        }

        this.startedAt = performance.now();

        const animate = now => {
            const offset = ((now - this.startedAt) * this.config.idleSpeed) % 360;
            this.render(1, offset);
            this.frame = requestAnimationFrame(animate);
        };

        this.frame = requestAnimationFrame(animate);
    },

    getTriggerElement() {
        const selector = this.$el.dataset.orbitTrigger;

        if (selector) {
            return document.querySelector(selector);
        }

        return this.$el.closest(".intro__sticker") || this.$el;
    },

    render(progress = 1, offset = 0) {
        const clampedProgress = Math.max(0, Math.min(1, progress));
        const activeLength = this.config.activeArc * clampedProgress;
        const startAngle = this.config.startAngle + offset;

        this.currentProgress = clampedProgress;
        this.currentOffset = offset;

        this.ticks.forEach(({ angle, line }) => {
            const distance = this.getAngleDistance(angle, startAngle);

            if (activeLength > 0 && distance <= activeLength) {
                const color = this.getActiveColor(distance / activeLength);

                line.setAttribute("stroke", color);
                line.setAttribute("opacity", "1");
                return;
            }

            line.setAttribute("stroke", this.config.inactiveColor);
            line.setAttribute("opacity", this.config.inactiveOpacity);
        });
    },

    getAngleDistance(angle, startAngle) {
        return this.normalizeAngle(angle - startAngle);
    },

    normalizeAngle(angle) {
        return ((angle % 360) + 360) % 360;
    },

    getActiveColor(progress) {
        const colors = this.config.colors;
        const segmentCount = colors.length - 1;
        const scaled = Math.min(progress, 0.9999) * segmentCount;
        const index = Math.floor(scaled);
        const segmentProgress = scaled - index;

        return this.mixColor(colors[index], colors[index + 1], segmentProgress);
    },

    mixColor(from, to, progress) {
        const a = this.hexToRgb(from);
        const b = this.hexToRgb(to);

        if (!a || !b) {
            return progress < 0.5 ? from : to;
        }

        const red = Math.round(a.red + (b.red - a.red) * progress);
        const green = Math.round(a.green + (b.green - a.green) * progress);
        const blue = Math.round(a.blue + (b.blue - a.blue) * progress);

        return `rgb(${red}, ${green}, ${blue})`;
    },

    hexToRgb(color) {
        const normalized = color.replace("#", "").trim();
        const hex = normalized.length === 3
            ? normalized.split("").map(character => character + character).join("")
            : normalized;

        if (!/^[0-9a-f]{6}$/i.test(hex)) {
            return null;
        }

        const value = parseInt(hex, 16);

        return {
            red: (value >> 16) & 255,
            green: (value >> 8) & 255,
            blue: value & 255,
        };
    },
});
